import { Engine } from './core/Engine.js';
import { EV } from './core/EventBus.js';
import { Lighting } from './render/Lighting.js';
import { Level } from './world/Level.js';
import { Player } from './player/Player.js';
import { LobbySystem } from './lobby/LobbySystem.js';

/**
 * LOBBY ou PARTIDA — a primeira decisão do boot, e a mais cara.
 *
 * Abrir o endereço limpo NÃO constrói mais a partida. Antes construía: 491
 * colisores, navgrid, 95 texturas procedurais, IA, rede, pós e FX, tudo antes
 * de perguntar o nome (medido em `tools/measure-ram.mjs`). Agora o endereço
 * limpo monta o lobby, e o mapa só nasce quando alguém clica em entrar.
 *
 * A regra é escrita ao contrário do que parece natural — "qualquer parâmetro
 * conhecido significa partida" — por um motivo prático: as ~30 ferramentas em
 * `tools/` abrem a página com `?map=`, `?room=`, `?mp=0`, `?name=`, `?server=`,
 * e todas esperam encontrar `level` e `player` prontos. Amarrando o modo aos
 * parâmetros que elas já usam, nenhuma precisou ser reescrita; as poucas que
 * abriam o endereço limpo ganharam um `play=1` explícito.
 *
 *   /                       -> lobby
 *   /?play=1                -> partida
 *   /?map=porto             -> partida (ferramenta, ou link compartilhado)
 *   /?lobby=1               -> lobby, mesmo com o resto na URL (para depurar)
 */
const PARAMS_DE_PARTIDA = ['play', 'map', 'mapa', 'room', 'mp', 'name', 'server', 'voice', 'quality'];

function decidirModo() {
  try {
    const q = new URLSearchParams(location.search);
    if (q.has('lobby')) return 'lobby';
    return PARAMS_DE_PARTIDA.some((k) => q.has(k)) ? 'jogo' : 'lobby';
  } catch {
    return 'jogo';
  }
}

const boot = document.getElementById('boot');
const bootMsg = document.getElementById('bootmsg');
const bootBar = document.getElementById('bootbar');

const step = (msg, pct) => {
  if (bootMsg) bootMsg.textContent = msg;
  if (bootBar) bootBar.style.width = `${pct}%`;
};

async function main() {
  const modo = decidirModo();

  step('creating renderer', 10);
  const engine = new Engine({ container: document.getElementById('app') });
  engine.modo = modo;

  if (modo === 'jogo') {
    step('building world', 30);
    engine.add(new Level());
    engine.add(new Lighting());
    engine.add(new Player());
  } else {
    step('preparando o lobby', 30);
    engine.add(new LobbySystem());
  }

  // Optional systems are attached only if their module is present, so a
  // subsystem that is still being built cannot break the whole boot.
  //
  // No lobby a lista é só a interface. Cada linha que NÃO entra aqui é memória
  // que ninguém paga por uma tela de menu: sem arma, sem FX, sem IA, sem rede,
  // sem voz, sem pós-processamento e — o mais caro de todos — sem nível.
  const optional = modo === 'jogo' ? [
    ['./render/PostFX.js', 'PostFX'],
    ['./weapons/WeaponSystem.js', 'WeaponSystem'],
    ['./fx/EffectsSystem.js', 'EffectsSystem'],
    ['./ai/AISystem.js', 'AISystem'],
    ['./audio/AudioSystem.js', 'AudioSystem'],
    ['./ui/HUD.js', 'HUD'],
    ['./net/NetSystem.js', 'NetSystem'],
    ['./world/Pickups.js', 'Pickups'],
    ['./voice/VoiceSystem.js', 'VoiceSystem'],
  ] : [
    ['./ui/HUD.js', 'HUD'],
  ];

  // import.meta.glob, não import() com @vite-ignore. Com @vite-ignore o vite
  // deixa o caminho intacto e não empacota nada, então em desenvolvimento tudo
  // carrega (o servidor entrega os arquivos avulsos) e em produção TODOS dão
  // 404 em silêncio — o jogo publicado subia sem arma, sem FX, sem pós e sem
  // rede, e nada no console dizia isso porque o catch tratava 404 como
  // "módulo opcional ausente". O glob é resolvido em build: os que existem
  // entram no bundle, os que não existem simplesmente não aparecem no mapa.
  const registry = import.meta.glob('./{render,weapons,fx,ai,audio,ui,net,world,voice}/*.js');

  step('loading systems', 45);
  const loaded = [];
  for (const [path, exportName] of optional) {
    const loader = registry[path];
    if (!loader) continue;               // subsistema ainda não escrito
    try {
      const mod = await loader();
      const Ctor = mod[exportName] ?? mod.default;
      if (Ctor) { engine.add(new Ctor()); loaded.push(exportName); }
      else console.warn(`[boot] ${path} existe mas não exporta ${exportName}`);
    } catch (err) {
      console.error(`[boot] ${exportName} falhou ao carregar`, err);
    }
  }
  console.log(`[boot] sistemas opcionais carregados: ${loaded.join(', ') || 'nenhum'}`);

  step('compiling shaders', 70);
  await engine.init();

  step('ready', 100);
  engine.start();

  // Click-to-play overlay: pointer lock must originate from a user gesture.
  // No lobby não existe: clicar na tela ali é clicar no menu, e prender o
  // cursor numa tela que é toda feita de botões só tira o cursor de quem está
  // tentando escolher alguma coisa.
  if (modo === 'jogo') {
    const startPlay = () => engine.input.requestLock();
    engine.renderer.domElement.addEventListener('click', startPlay);
    engine.bus.on(EV.POINTER_LOCK, ({ locked }) => {
      if (locked) engine.resume();
      else engine.pause();
      document.body.classList.toggle('locked', locked);
    });
  }

  setTimeout(() => {
    boot.style.transition = 'opacity .5s';
    boot.style.opacity = '0';
    setTimeout(() => boot.remove(), 600);
  }, 250);

  // Screenshot/automation hook. The harness drives the camera through this so
  // captures are deterministic and do not need pointer lock.
  window.__GAME__ = {
    engine,
    version: 2,
    /** 'lobby' ou 'jogo'. As ferramentas conferem isto antes de pedir o nível. */
    modo,
    /** Place the camera and render one frame synchronously. */
    setView({ x, y, z, yaw, pitch, fov }) {
      const p = engine.get('player');
      if (!p) return false;
      // Qualquer captura automatizada dirige a câmera por aqui, e nunca entra em
      // pointer lock. Fechar a tela de entrada neste ponto evita ter que passar
      // uma flag de headless para cada ferramenta — quem dirige por código já
      // demonstrou que não é um jogador esperando o painel.
      engine.get('hud')?.entry?.hide?.();
      if (x !== undefined) p.position.set(x, y, z);
      if (yaw !== undefined) p.yaw = yaw;
      if (pitch !== undefined) p.pitch = pitch;
      if (fov !== undefined) { engine.camera.fov = fov; engine.camera.updateProjectionMatrix(); }
      p._updateCamera(0.016, engine);
      return true;
    },
    /**
     * Drive the game for a screenshot without pointer lock or real input.
     * Each action synthesises the same state the input layer would produce, so
     * captures exercise the real code path rather than a screenshot-only branch.
     */
    act(kind) {
      engine.get('hud')?.entry?.hide?.();
      const input = engine.input;
      const weapons = engine.get('weapons') || engine.get('weaponSystem');
      const player = engine.get('player');
      const ai = engine.get('ai');
      const hold = (action) => { input.down.add(action); input.pressed.add(action); };

      switch (kind) {
        case 'fire':
          hold('fire');
          weapons?.tryFire?.();
          break;
        case 'reload':
          hold('reload');
          weapons?.reload?.();
          break;
        case 'sprint':
          hold('forward'); hold('sprint');
          if (player) { player.velocity.set(0, 0, -7); player._sprintCharge = 1; }
          break;
        case 'combat':
          // Wake the AI and put rounds in the air so FX, HUD and enemies are
          // all mid-flight when the frame is grabbed.
          ai?.alertAll?.();
          ai?.spawnWave?.();
          hold('fire');
          weapons?.tryFire?.();
          break;
      }
      return true;
    },
    /** Release everything the harness synthesised. */
    release() {
      engine.input.down.clear();
      engine.input.pressed.clear();
      return true;
    },
    stats() {
      return {
        modo,
        fps: Math.round(engine.time.fps),
        ms99: +engine.time.ms99.toFixed(2),
        calls: engine.renderer.info.render.calls,
        tris: engine.renderer.info.render.triangles,
        programs: engine.renderer.info.programs?.length ?? 0,
        quality: engine.quality,
        systems: engine.systems.map((s) => s.name || s.constructor.name),
      };
    },
  };
  window.__READY__ = true;
  console.log('[boot] ready', window.__GAME__.stats());
}

main().catch((err) => {
  console.error(err);
  window.__BOOT_ERROR__ = String(err?.stack || err);
  if (bootMsg) { bootMsg.textContent = 'boot failed — see console'; bootMsg.style.color = '#e05a4a'; }
});
